'use client';

import { useId, useState } from 'react';
import { trackPublicToolAction } from '@/lib/analytics/public-tools';
import styles from './ResultExport.module.css';

export function ResultExport({
  text,
  filename,
  tool,
}: {
  text: string;
  filename: string;
  tool: string;
}) {
  const [feedback, setFeedback] = useState<{
    text: string;
    message: string;
    manual?: boolean;
  } | null>(null);
  const id = useId();
  const current = feedback?.text === text ? feedback : null;
  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setFeedback({ text, message: 'Copied with the working and assumptions.' });
      void trackPublicToolAction({ tool, action: 'copy', format: 'txt' });
    } catch {
      setFeedback({
        text,
        message: 'Copy is unavailable in this browser. Select the text below, or download it.',
        manual: true,
      });
    }
  }
  function download() {
    try {
      const url = URL.createObjectURL(
        new Blob([text + '\n'], { type: 'text/plain;charset=utf-8' }),
      );
      const link = document.createElement('a');
      link.href = url;
      link.download = `${filename}.txt`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      setFeedback({ text, message: 'Download started. Add the reporting details before sharing.' });
      void trackPublicToolAction({ tool, action: 'download', format: 'txt' });
    } catch {
      setFeedback({
        text,
        message: 'Download is unavailable. Select the text below to save it.',
        manual: true,
      });
    }
  }
  return (
    <div className={styles.root}>
      <p className={styles.label}>Keep your result</p>
      <p className={styles.hint}>Includes your inputs, the working and what the result means.</p>
      <div className={styles.actions}>
        <button type="button" onClick={copy}>
          Copy result
        </button>
        <button type="button" onClick={download}>
          Download result <span>TXT</span>
        </button>
      </div>
      <p className={styles.feedback} aria-live="polite">
        {current?.message}
      </p>
      {current?.manual ? (
        <>
          <label className={styles.hint} htmlFor={id}>
            Result text
          </label>
          <textarea
            id={id}
            readOnly
            value={text}
            rows={9}
            onFocus={(event) => event.currentTarget.select()}
            onClick={(event) => event.currentTarget.select()}
          />
        </>
      ) : null}
    </div>
  );
}
